import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { chaveOnboardingConcluido, obterEhAdmin, obterEmailUsuario, obterIdTenent, obterNomeUsuario } from '../../services/seguranca';
import { AcessoLocatario, onboardingService, OnboardingStatus } from '../../services/onboarding';
import { rastrearTeste, segundosDesde } from '../../services/rastreamentoTeste';
import EtapaDores from './EtapaDores';
import EtapaFrota from './EtapaFrota';
import EtapaContrato from './EtapaContrato';
import EtapaLocacao from './EtapaLocacao';
import EtapaContratoPreenchido from './EtapaContratoPreenchido';
import EtapaAppLocatario from './EtapaAppLocatario';
import EtapaAppLocador from './EtapaAppLocador';
import EtapaFinal from './EtapaFinal';
import { FaFlask } from 'react-icons/fa';
import { BarraCheia, BarraFundo, FaixaExperimento, Painel, Tela, Topo } from './styles';

const ETAPAS = [
    { chave: 'dores', rotulo: 'Prioridades' },
    { chave: 'frota', rotulo: 'Frota' },
    { chave: 'contrato', rotulo: 'Contrato' },
    { chave: 'locacao', rotulo: 'Primeira locação' },
    { chave: 'contratoPreenchido', rotulo: 'Contrato preenchido' },
    { chave: 'appLocatario', rotulo: 'App do Locatário' },
    { chave: 'appLocador', rotulo: 'App do Locador' },
    { chave: 'final', rotulo: 'Concluído' },
] as const;

type ChaveEtapa = typeof ETAPAS[number]['chave'];

interface Progressao {
    etapa: ChaveEtapa;
    idLocacao?: string;
    idModeloContrato?: string;
    /** Acesso do locatário já gerado para esta locação: voltar à etapa não troca a senha dele. */
    acessoLocatario?: AcessoLocatario & { idLocacao: string };
}

const ETAPAS_DEPOIS_DA_LOCACAO: ChaveEtapa[] = ['contratoPreenchido', 'appLocatario', 'appLocador', 'final'];

/** Progresso gravado neste navegador, ou null quando não há (outro aparelho, aba anônima). */
const lerProgressaoSalva = (chave: string): Progressao | null => {
    try {
        const salvo = JSON.parse(localStorage.getItem(chave) || 'null');
        if (salvo && ETAPAS.some(e => e.chave === salvo.etapa)) return salvo;
    } catch {
        localStorage.removeItem(chave);
    }
    return null;
};

/**
 * Sem progresso local, retoma pelo que já existe no servidor: com a locação criada, segue para depois
 * dela (não cria outra); com veículo ou contrato, volta para a locação. Com progresso local, ele manda;
 * a única correção é a locação que já existe no servidor quando o navegador ainda estava nela (recarga
 * logo depois de salvar), para não criar a mesma locação de novo.
 */
const retomar = (salva: Progressao | null, status: OnboardingStatus): Progressao => {
    const idLocacao = status.idLocacao || undefined;
    const idModeloContrato = status.idModeloContrato || undefined;
    const depoisDaLocacao = (modelo?: string): ChaveEtapa => (modelo ? 'contratoPreenchido' : 'appLocatario');

    if (salva) {
        if (idLocacao && salva.etapa === 'locacao')
            return { ...salva, etapa: depoisDaLocacao(salva.idModeloContrato ?? idModeloContrato), idLocacao, idModeloContrato: salva.idModeloContrato ?? idModeloContrato };
        if (idLocacao && !salva.idLocacao && ETAPAS_DEPOIS_DA_LOCACAO.includes(salva.etapa))
            return { ...salva, idLocacao };
        return salva;
    }
    if (idLocacao) return { etapa: depoisDaLocacao(idModeloContrato), idLocacao, idModeloContrato };
    if ((status.totalVeiculos ?? 0) > 0 || idModeloContrato) return { etapa: 'locacao', idModeloContrato };
    return { etapa: 'dores' };
};

export default function Onboarding() {
    const history = useHistory();
    const token = useAppSelector(state => state.token);
    const chaveProgressao = `onboarding_etapa_${obterIdTenent(token)}`;
    const [status, setStatus] = useState<OnboardingStatus | null>(null);
    const [dores, setDores] = useState<string[]>([]);
    const [progressao, setProgressao] = useState<Progressao>(() => lerProgressaoSalva(chaveProgressao) ?? { etapa: 'dores' });
    // Cópia síncrona do progresso: a locação grava o seu id antes de a etapa trocar.
    const progressaoRef = useRef(progressao);

    const gravar = useCallback((nova: Progressao) => {
        progressaoRef.current = nova;
        try { localStorage.setItem(chaveProgressao, JSON.stringify(nova)); } catch { /* sem armazenamento: segue só na memória */ }
    }, [chaveProgressao]);

    // Etapa à vista e desde quando (nesta visita), para medir quanto tempo a pessoa ficou nela.
    const etapaAberta = useRef<{ etapa: ChaveEtapa; desde: number } | null>(null);

    const finalizar = useCallback((rota: string) => {
        if (etapaAberta.current?.etapa === 'final') {
            rastrearTeste('onboarding_etapa_concluida', { etapa: 'final', segundos: segundosDesde(etapaAberta.current.desde) });
            etapaAberta.current = null;
        }
        // No painel quem abre são as prioridades; o convite de treinamento fica para o próximo acesso.
        sessionStorage.setItem('modalTreinamentoVisto', 'true');
        // O checklist antigo de "Primeiros Passos" abriria sozinho por cima das prioridades; o
        // onboarding já cobriu esses passos. Ele continua no botão "Primeiros Passos".
        localStorage.setItem('locasmart_onboarding_dismissed', 'true');
        localStorage.setItem(chaveOnboardingConcluido(token), token);
        localStorage.removeItem(chaveProgressao);
        history.replace(rota);
    }, [token, chaveProgressao, history]);

    useEffect(() => {
        if (!token) {
            history.replace('/');
            return;
        }
        // Só o administrador passa pelo guia; os demais vão direto para o sistema.
        if (!obterEhAdmin(token)) {
            history.replace('/home');
            return;
        }
        onboardingService.status()
            .then(s => {
                if (!s.pendente) {
                    finalizar('/home');
                    return;
                }
                localStorage.removeItem(chaveOnboardingConcluido(token));
                const dores = s.dores ?? [];
                const salva = lerProgressaoSalva(chaveProgressao);
                let inicial: Progressao;
                if (dores.length === 0 && !s.idLocacao) {
                    localStorage.removeItem(chaveProgressao);
                    inicial = { etapa: 'dores' };
                } else {
                    inicial = retomar(salva, s);
                    if (inicial !== salva) gravar(inicial);
                }
                progressaoRef.current = inicial;
                setProgressao(inicial);
                // As prioridades antes do status: no React 17 cada setState aqui renderiza sozinho, e a
                // etapa de prioridades só aparece com o status; assim ela já nasce com as escolhas.
                setDores(dores);
                setStatus({ ...s, dores });
            })
            .catch(() => setStatus({ pendente: true, dores: [], placasRestantes: 10, limitePlacas: 10 }));
    }, [token]);

    // Veículos livres conferidos ao chegar na locação: o status do começo é de antes da frota.
    const [livresNaLocacao, setLivresNaLocacao] = useState<number | null | undefined>(undefined);
    useEffect(() => {
        if (progressao.etapa !== 'locacao') { setLivresNaLocacao(undefined); return; }
        let ativo = true;
        onboardingService.status()
            .then(s => { if (ativo) setLivresNaLocacao(s.veiculosLivres ?? null); })
            .catch(() => { if (ativo) setLivresNaLocacao(null); });
        return () => { ativo = false; };
    }, [progressao.etapa]);

    // Uma "aberta" por etapa que aparece e uma "concluída" ao sair dela para outra. Só depois do
    // status: antes dele a etapa ainda pode ser corrigida pela retomada.
    useEffect(() => {
        if (!status) return;
        const anterior = etapaAberta.current;
        if (anterior?.etapa === progressao.etapa) return;
        if (anterior) rastrearTeste('onboarding_etapa_concluida', { etapa: anterior.etapa, segundos: segundosDesde(anterior.desde) });
        etapaAberta.current = { etapa: progressao.etapa, desde: Date.now() };
        rastrearTeste('onboarding_etapa_aberta', { etapa: progressao.etapa });
    }, [status, progressao.etapa]);

    const avancar = (etapa: ChaveEtapa, dados: Partial<Progressao> = {}) => {
        const nova = { ...progressaoRef.current, ...dados, etapa };
        gravar(nova);
        setProgressao(nova);
        window.scrollTo({ top: 0 });
    };

    // Com modelo de contrato, o locador vê o contrato preenchido antes de seguir.
    const etapaDepoisDaLocacao = (): ChaveEtapa => (progressaoRef.current.idModeloContrato ? 'contratoPreenchido' : 'appLocatario');

    const registrarLocacaoCriada = (idLocacao: string) =>
        gravar({ ...progressaoRef.current, etapa: etapaDepoisDaLocacao(), idLocacao });

    const guardarAcessoLocatario = (idLocacao: string, acesso: AcessoLocatario) => {
        const nova = { ...progressaoRef.current, acessoLocatario: { ...acesso, idLocacao } };
        gravar(nova);
        setProgressao(nova);
    };

    const indice = ETAPAS.findIndex(e => e.chave === progressao.etapa);
    const totalVisivel = ETAPAS.length - 1;
    const pct = Math.round((indice / totalVisivel) * 100);
    const semLocacao = (progressao.etapa === 'appLocatario' || progressao.etapa === 'contratoPreenchido') && !progressao.idLocacao;

    return (
        <Tela>
            <Topo>
                <span>
                    {progressao.etapa === 'final' ? 'Configuração concluída' : `Etapa ${indice + 1} de ${totalVisivel} · ${ETAPAS[indice].rotulo}`}
                </span>
                <BarraFundo><BarraCheia $pct={pct} /></BarraFundo>
                <FaixaExperimento>
                    <FaFlask />
                    <span><strong>Você está experimentando o sistema.</strong> Nada aqui é definitivo: depois você configura tudo do seu jeito, com a ajuda dos nossos técnicos.</span>
                </FaixaExperimento>
            </Topo>

            {/* A primeira locação é o formulário do sistema, com a cara do modal, direto sobre o fundo. */}
            {status && (progressao.etapa === 'locacao' || semLocacao) && (progressao.etapa !== 'locacao' || livresNaLocacao !== undefined) && (
                <EtapaLocacao idModeloContrato={progressao.idModeloContrato}
                    semVeiculoLivre={livresNaLocacao === 0}
                    registrarLocacaoCriada={registrarLocacaoCriada}
                    aoCriarLocacao={idLocacao => avancar(etapaDepoisDaLocacao(), { idLocacao })} />
            )}

            {status && !(progressao.etapa === 'locacao' || semLocacao) && (
                <Painel $compacto={progressao.etapa === 'contratoPreenchido'}>
                    {progressao.etapa === 'dores' && (
                        <EtapaDores nomeUsuario={obterNomeUsuario(token)} doresIniciais={dores} aoConcluir={d => { setDores(d); avancar('frota'); }} />
                    )}
                    {progressao.etapa === 'frota' && (
                        <EtapaFrota aoConcluir={() => avancar('contrato')} />
                    )}
                    {progressao.etapa === 'contrato' && (
                        <EtapaContrato aoConcluir={idModeloContrato => avancar('locacao', { idModeloContrato })} />
                    )}
                    {progressao.etapa === 'contratoPreenchido' && progressao.idLocacao && (
                        <EtapaContratoPreenchido idLocacao={progressao.idLocacao} aoConcluir={() => avancar('appLocatario')} />
                    )}
                    {progressao.etapa === 'appLocatario' && progressao.idLocacao && (
                        <EtapaAppLocatario idLocacao={progressao.idLocacao}
                            acessoSalvo={progressao.acessoLocatario?.idLocacao === progressao.idLocacao ? progressao.acessoLocatario : undefined}
                            aoGerarAcesso={acesso => guardarAcessoLocatario(progressao.idLocacao!, acesso)}
                            aoConcluir={() => avancar('appLocador')} />
                    )}
                    {progressao.etapa === 'appLocador' && (
                        <EtapaAppLocador email={obterEmailUsuario(token)} aoConcluir={() => avancar('final')} />
                    )}
                    {progressao.etapa === 'final' && <EtapaFinal aoFinalizar={finalizar} />}
                </Painel>
            )}
        </Tela>
    );
}
